import React, { useState } from "react";
import { getAuthHeaders } from "./AuthGate";

const API_BASE = "/api/diagrams";

interface DiagramListItem {
  id: string;
  name: string;
  thumbnail: string | null;
  created_at: string;
  updated_at: string;
}

// Save current scene to server
export const saveToCloud = async (
  elements: readonly any[],
  appState: any,
  name?: string,
): Promise<{ id: string; name: string } | null> => {
  const diagramName =
    name || prompt("Nom du diagramme :") || `Diagramme ${new Date().toLocaleDateString("fr-FR")}`;

  try {
    const response = await fetch(API_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({
        name: diagramName,
        data: { elements, appState: { viewBackgroundColor: appState.viewBackgroundColor } },
      }),
    });
    const result = await response.json();
    if (result.id) {
      // Store current diagram ID in URL hash for future saves
      window.location.hash = `cloud=${result.id}`;
      return result;
    }
  } catch (e) {
    console.error("Save failed:", e);
  }
  return null;
};

// Update existing diagram on server
export const updateOnCloud = async (
  id: string,
  elements: readonly any[],
  appState: any,
): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({
        data: { elements, appState: { viewBackgroundColor: appState.viewBackgroundColor } },
      }),
    });
    return response.ok;
  } catch (e) {
    console.error("Update failed:", e);
    return false;
  }
};

// Load diagram from server (cache-busted)
export const loadFromCloud = async (
  id: string,
): Promise<{ elements: any[]; appState: any } | null> => {
  try {
    const response = await fetch(`${API_BASE}/${id}?t=${Date.now()}`, {
      headers: { ...getAuthHeaders(), "Cache-Control": "no-cache" },
    });
    if (!response.ok) return null;
    const diagram = await response.json();
    const data = typeof diagram.data === "string" ? JSON.parse(diagram.data) : diagram.data;
    return data;
  } catch (e) {
    console.error("Load failed:", e);
    return null;
  }
};

// Get current cloud ID from URL hash
export const getCloudId = (): string | null => {
  const match = window.location.hash.match(/cloud=([a-f0-9-]+)/);
  return match ? match[1] : null;
};

// Clear cloud ID (for new diagram)
export const clearCloudId = (): void => {
  window.location.hash = "";
};

// List all diagrams component
export const DiagramGallery: React.FC<{
  onLoad: (data: { elements: any[]; appState: any }) => void;
  onClose: () => void;
}> = ({ onLoad, onClose }) => {
  const [diagrams, setDiagrams] = useState<DiagramListItem[]>([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetch(`${API_BASE}?t=${Date.now()}`, {
      headers: { ...getAuthHeaders(), "Cache-Control": "no-cache" },
    })
      .then((r) => r.json())
      .then((d) => {
        setDiagrams(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm("Supprimer ce diagramme ?")) return;
    await fetch(`${API_BASE}/${id}`, { method: "DELETE", headers: getAuthHeaders() });
    setDiagrams((prev) => prev.filter((d) => d.id !== id));
  };

  const handleLoad = async (id: string) => {
    setLoading(true);
    const data = await loadFromCloud(id);
    if (data) {
      window.history.replaceState(null, "", `#cloud=${id}`);
      onLoad(data);
      onClose();
    } else {
      setLoading(false);
      alert("Erreur lors du chargement du diagramme");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 24,
          width: 600,
          maxHeight: "70vh",
          overflow: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontFamily: "Inter, sans-serif" }}>Mes diagrammes</h2>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>
            x
          </button>
        </div>

        {loading && <p style={{ color: "#666", fontFamily: "Inter, sans-serif" }}>Chargement...</p>}

        {!loading && diagrams.length === 0 && (
          <p style={{ color: "#999", fontFamily: "Inter, sans-serif", textAlign: "center", padding: 40 }}>
            Aucun diagramme sauvegarde
          </p>
        )}

        {diagrams.map((d) => (
          <div
            key={d.id}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 12px",
              borderBottom: "1px solid #eee",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{d.name}</div>
              <div style={{ fontSize: 11, color: "#999" }}>
                {new Date(d.updated_at).toLocaleDateString("fr-FR")} a{" "}
                {new Date(d.updated_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => handleLoad(d.id)}
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "1px solid #4285F4",
                  background: "#e8f0fe",
                  color: "#1a73e8",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Ouvrir
              </button>
              <button
                onClick={() => handleDelete(d.id)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  background: "#fff",
                  color: "#999",
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
